import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'

const colors = {
  primary: '#465775',
  primaryLight: '#edf1f8',
  primaryDark: '#2c3b55',
  accent: '#b64f69',
  accentLight: '#fbeef2',
  canvas: '#f4f6fa',
  paper: '#ffffff',
  text: '#273449',
  secondaryText: '#64748b',
  divider: '#e7ebf2',
}

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
    },
    secondary: {
      main: colors.accent,
      light: colors.accentLight,
      dark: '#8e3650',
    },
    background: { default: colors.canvas, paper: colors.paper },
    text: { primary: colors.text, secondary: colors.secondaryText },
    divider: colors.divider,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 650, letterSpacing: '-0.03em' },
    h5: { fontWeight: 650, letterSpacing: '-0.025em' },
    h6: { fontWeight: 600, letterSpacing: '-0.015em' },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.canvas,
          color: colors.text,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default' },
      styleOverrides: {
        root: {
          color: colors.text,
          backgroundColor: colors.paper,
          boxShadow: '0 1px 3px rgba(39, 52, 73, 0.08)',
          borderBottom: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 64 },
      },
    },
    MuiGrid: {
      styleOverrides: {
        // Legacy Grid containers filled their parent. MUI 9's Grid shrinks
        // without an explicit width, which moves space-between actions.
        container: { width: '100%' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
        elevation1: {
          boxShadow: '0 2px 6px rgba(39, 52, 73, 0.025), 0 0 0 1px #e7ebf2',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 8,
          padding: '8px 16px',
          '&.Mui-focusVisible': {
            outline: '3px solid #c4cedf',
            outlineOffset: 2,
          },
        },
        sizeSmall: { minHeight: 32, padding: '4px 12px' },
        sizeLarge: { minHeight: 48, padding: '10px 20px' },
        outlined: { borderColor: '#cbd4e2' },
      },
    },
    MuiTabs: {
      defaultProps: { allowScrollButtonsMobile: true },
      styleOverrides: {
        root: { minHeight: 48, borderBottom: `1px solid ${colors.divider}` },
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
        list: { gap: 4 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          padding: '12px 18px',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          color: colors.secondaryText,
          '&:hover': { backgroundColor: '#f5f7fb', color: colors.text },
          '&.Mui-selected': { color: colors.primary, fontWeight: 650 },
          '&.Mui-focusVisible': { backgroundColor: colors.primaryLight },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: colors.paper,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9aa9be',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(70, 87, 117, 0.08)',
          },
          '&.Mui-disabled': { backgroundColor: '#f6f7fa' },
          '&.MuiInputBase-multiline': { padding: '10px 14px' },
        },
        input: {
          padding: '11px 14px',
          '.MuiInputBase-sizeSmall &': { padding: '8px 12px' },
        },
        notchedOutline: { borderColor: '#d4dce8' },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          // Match single-line fields without fixing the height of chip rows.
          '& .MuiOutlinedInput-root': {
            paddingTop: 3.5,
            paddingBottom: 3.5,
          },
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: 5.5,
            paddingBottom: 5.5,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: colors.paper,
          '& .MuiPickersInputBase-sectionsContainer': {
            paddingTop: 11,
            paddingBottom: 11,
          },
          '&.MuiPickersInputBase-sizeSmall .MuiPickersInputBase-sectionsContainer': {
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
        notchedOutline: { borderColor: '#d4dce8' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.secondaryText,
          '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 11px) scale(1)',
          },
          '&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': {
            transform: 'translate(12px, 8px) scale(1)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginTop: 6 } },
    },
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${colors.divider}` },
        head: {
          backgroundColor: '#f8f9fc',
          color: colors.secondaryText,
          fontWeight: 600,
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          paddingTop: 14,
          paddingBottom: 14,
        },
        body: { fontSize: '0.875rem', lineHeight: 1.5 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: '#f5f7fb' },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: { toolbar: { color: colors.secondaryText } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600 },
        sizeSmall: { height: 26, fontSize: '0.75rem' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14, boxShadow: '0 24px 80px rgba(39, 52, 73, 0.2)' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { padding: '20px 24px', fontWeight: 600 } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '16px 24px', gap: 8 },
        spacing: { '& > :not(:first-of-type)': { marginLeft: 0 } },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiCircularProgress: {
      defaultProps: { 'aria-label': 'Loading' },
    },
  },
})
